import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import api from "../services/api";
import ClinicPackageCard from "../components/ui/ClinicPackageCard";
import { getImageUrl } from "../lib/utils";

const DEFAULTS = {
  bannerImages: [],
  bannerTitle: "New Clinic Setup",
  bannerSubtitle: "Everything you need to set up your clinic, in one place.",
};

const NewClinicSetupPage = () => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [activeType, setActiveType] = useState("all");
  const [sortOrder, setSortOrder] = useState("recommended");

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchSettings = async () => {
      try {
        const response = await api.get("/settings/clinic-setup");
        if (response.data.success) {
          setSettings({ ...DEFAULTS, ...response.data.data });
        }
      } catch (error) {
        console.error("Error fetching clinic setup settings:", error);
      }
    };
    const fetchPackages = async () => {
      try {
        const response = await api.get("/clinic-setup-packages");
        if (response.data.success) {
          setPackages(response.data.data || []);
        }
      } catch (error) {
        console.error("Error fetching clinic setup packages:", error);
      } finally {
        setPackagesLoading(false);
      }
    };
    fetchSettings();
    fetchPackages();
  }, []);

  const packageTypes = useMemo(
    () => Array.from(new Set(packages.map((pkg) => pkg.type).filter(Boolean))),
    [packages],
  );

  const visiblePackages = useMemo(() => {
    let list =
      activeType === "all"
        ? packages
        : packages.filter((pkg) => pkg.type === activeType);
    list = [...list];
    if (sortOrder === "price-asc") list.sort((a, b) => a.price - b.price);
    if (sortOrder === "price-desc") list.sort((a, b) => b.price - a.price);
    return list;
  }, [packages, activeType, sortOrder]);

  return (
    <div className="w-full bg-white">
      {/* Breadcrumb */}
      <div className="container mx-auto px-4 pt-4">
        <nav className="flex items-center gap-1.5 text-xs text-gray-500">
          <Link to="/" className="hover:text-gray-700 transition-colors">
            Home
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-secondary-600 font-medium">
            New Clinic Setup
          </span>
        </nav>
      </div>

      {/* Hero Banner */}
      <div className="container mx-auto px-4 pt-3">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 via-white to-rose-50 border border-gray-100">
          {settings.bannerImages.length > 0 ? (
            <div className="relative clinic-hero-swiper">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={0}
                slidesPerView={1}
                navigation={settings.bannerImages.length > 1}
                pagination={
                  settings.bannerImages.length > 1 ? { clickable: true } : false
                }
                loop={settings.bannerImages.length > 1}
                autoplay={
                  settings.bannerImages.length > 1
                    ? { delay: 5000, disableOnInteraction: false }
                    : false
                }
                className="w-full"
              >
                {settings.bannerImages.map((image, index) => (
                  <SwiperSlide key={image + index}>
                    <img
                      src={getImageUrl(image)}
                      alt={`${settings.bannerTitle} - slide ${index + 1}`}
                      className="w-full h-64 sm:h-80 md:h-[420px] object-cover"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="absolute inset-0 z-10 flex items-center justify-start p-6 sm:p-12 md:p-16 pointer-events-none">
                <div className="relative max-w-xl pointer-events-auto">
                  <div className="absolute -inset-x-6 -inset-y-10 sm:-inset-x-10 sm:-inset-y-12 -z-10 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.5)_0%,rgba(0,0,0,0.22)_50%,transparent_75%)]" />
                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-white [text-shadow:0_2px_10px_rgba(0,0,0,0.55)]">
                    {settings.bannerTitle}
                  </h1>
                  <p className="text-sm md:text-base mt-3 text-white/95 leading-relaxed [text-shadow:0_1px_6px_rgba(0,0,0,0.5)]">
                    {settings.bannerSubtitle}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="min-h-64 sm:min-h-80 md:min-h-[420px] flex flex-col justify-center px-6 py-10 sm:px-10 sm:py-16">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-[#1B365D]">
                {settings.bannerTitle}
              </h1>
              <p className="text-sm md:text-base mt-2 text-gray-600 max-w-xl">
                {settings.bannerSubtitle}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Setup Packages */}
      <div id="setup-packages" className="container mx-auto px-4 py-8 sm:py-12 border-b border-gray-100 scroll-mt-20">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-[#1B365D] mb-1">
              Choose Your Setup Package
            </h2>
            <div className="w-12 h-0.5 bg-secondary-600" />
          </div>

          {packageTypes.length > 0 && (
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="self-start sm:self-auto text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 focus:outline-none focus:ring-1 focus:ring-secondary-400"
            >
              <option value="recommended">Sort: Recommended</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
            </select>
          )}
        </div>

        {packageTypes.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-8">
            <button
              onClick={() => setActiveType("all")}
              className={`text-xs md:text-sm py-1.5 px-3.5 rounded-full font-medium transition-colors ${
                activeType === "all"
                  ? "bg-secondary-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All Packages
            </button>
            {packageTypes.map((type) => (
              <button
                key={type}
                onClick={() => setActiveType(type)}
                className={`text-xs md:text-sm py-1.5 px-3.5 rounded-full font-medium transition-colors ${
                  activeType === type
                    ? "bg-secondary-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        )}

        {packagesLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 md:gap-6">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="rounded-2xl border border-gray-100 overflow-hidden"
              >
                <div className="aspect-square bg-admin-slate-100 animate-pulse" />
                <div className="p-1.5 space-y-2">
                  <div className="h-4 w-3/4 bg-admin-slate-100 rounded animate-pulse" />
                  <div className="h-3 w-full bg-admin-slate-100 rounded animate-pulse" />
                  <div className="h-4 w-1/2 bg-admin-slate-100 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : visiblePackages.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-gray-200 rounded-2xl">
            <p className="text-gray-500 text-sm">
              Setup packages will be listed here soon. Contact us for a custom
              quote in the meantime.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 md:gap-6">
            {visiblePackages.map((pkg) => (
              <ClinicPackageCard key={pkg._id} pkg={pkg} />
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default NewClinicSetupPage;
